import type { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Footer } from "components/modules/Layout/Footer/Footer";
import Head from "next/head";
import { url } from "client/config/url";
import { commonLanguageFiles } from "client/config";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { translateFunc } from "client/utils/translateFunc";
import { MemeGeneratorContent } from "components/modules/Content/MemeGeneratorContent/MemeGeneratorContent";
import { MemeGeneratorTutorial } from "components/modules/Tutorial/MemeGeneratorTutorial/MemeGeneratorTutorial";
import type { MemeTemplate } from "types/meme";
import { SSRConfig } from "next-i18next";
import { fetchMemes } from "lib/memeGenerator/fetchMemes";
import translatedRoutedSlugs from "translatedRouteSlugs";
import dynamic from "next/dynamic";
import type { MemeGeneratorProps } from "components/modules/Editors/MemeGenerator/MemeGenerator";
import { TrustPilotSection } from "components/modules/TrustPilot/TrustPilotSection";
import React from "react";

interface PageProps {
    memes: Array<MemeTemplate>;
    defaultMeme: MemeTemplate;
}

const MemeGenerator = dynamic<MemeGeneratorProps>(() =>
    import("components/modules/Editors/MemeGenerator/MemeGenerator").then((mod) => mod.MemeGenerator),
);

const Page = ({ memes, defaultMeme }: PageProps) => {
    const title = translateFunc("title");
    const description = translateFunc("meta-description");
    const defaultPageSlug = translatedRoutedSlugs["/meme-generator"]["en"];

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:url" content={`${url}/${defaultPageSlug}`} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
                <meta property="twitter:description" content={description} />
            </Head>
            <div className="max-w-1440 page-shadow dark:bg-darkSurface-100">
                <MemeGenerator initialMemes={memes} defaultMeme={defaultMeme} />
                <div className="pb-12 max-w-1440 border-x md:pb-0">
                    <MemeGeneratorContent />
                    <MemeGeneratorTutorial />
                    <TrustPilotSection />
                    <Footer url="/meme-generator" />
                </div>
            </div>
        </>
    );
};

const files = ["meme-generator", "uploader", ...commonLanguageFiles];

const LanguageWrapper = (props: PageProps) => (
    <LanguageProvider files={files}>
        <Page {...props} />
    </LanguageProvider>
);

export const getStaticProps: GetStaticProps<
    { memes: Array<MemeTemplate>; defaultMeme: MemeTemplate } & SSRConfig
> = async ({ locale }) => {
    const translationFiles = await serverSideTranslations(locale ?? "", files);
    const { memes, error } = await fetchMemes({ q: "", page: 1 });
    if (!memes || error) throw new Error("Could not find meme");
    return {
        props: {
            memes,
            defaultMeme: memes[0],
            ...translationFiles,
        },
        revalidate: 60 * 60, // one hour
    };
};

export default LanguageWrapper;
